import { FC, useCallback, useMemo } from 'react';
import { useAtom } from 'jotai';
import { Link } from 'components/link/link';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import type { TableState } from 'components/data-table/types';
import {
  AtomisedNotifications,
  NotificationGroup,
} from 'components/atomised-notifications';
import { createDifferentialScenarioSearchAtom } from './atoms';
import type { DifferentialScenarioResult } from './types';
import { DifferentialScenariosFilters } from './differential-scenarios-filters';
import { DifferentialSummaryCards } from './summary-cards';
import { CloneButton } from './clone-button';
import { columnFiltersToParams, SORT_FIELD_MAP } from './utils';
import styles from './differential-scenarios-content.module.scss';

type DifferentialScenarioRow = DifferentialScenarioResult &
  Record<string, unknown>;

/**
 * Main content for the Differential Scenarios list page — summary cards +
 * DataTable wired through a search-atom pair. A single `loadData` callback
 * funnels pagination, sort, filter, and global-search changes directly into
 * the search atom; the atom's own onMount fires the initial fetch.
 * @return {JSX.Element} The list page content
 */
const DifferentialScenariosContent: FC = () => {
  const { searchAtom } = useMemo(
    () => createDifferentialScenarioSearchAtom(),
    []
  );
  const [searchResult, setSearchPayload] = useAtom(searchAtom);

  // Re-fires the current search so a successful clone surfaces in the list.
  // Returning `undefined` when `current` is missing lets the atom's
  // `defaultPayload` kick in (returning `{}` would bypass that and POST an
  // empty body).
  const refreshList = useCallback(() => {
    setSearchPayload((current) => current && { ...current });
  }, [setSearchPayload]);

  const columns = useMemo<DataTableColumnDef<DifferentialScenarioRow>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        size: 280,
        meta: { wrap: true },
        cell: ({ row }) => (
          <Link to={row.original.href}>{row.original.name}</Link>
        ),
      },
      {
        accessorKey: 'areaCount',
        header: '# of Areas',
        size: 120,
        meta: { type: 'integer', wrap: true },
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: 'differentialCount',
        header: '# of Differentials',
        size: 140,
        meta: { type: 'integer', wrap: true },
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: 'effectiveDate',
        header: 'Effective Date',
        size: 140,
        meta: { type: 'date', wrap: true },
      },
      {
        accessorKey: 'createdBy',
        header: 'Created By',
        size: 160,
        meta: { type: 'enum', wrap: true },
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: 'createdAt',
        header: 'Created At',
        size: 180,
        meta: { type: 'date', wrap: true },
      },
      {
        accessorKey: 'updatedAt',
        header: 'Updated At',
        size: 180,
        meta: { type: 'date', wrap: true },
      },
      {
        id: 'actions',
        header: '',
        size: 60,
        enableSorting: false,
        enableColumnFilter: false,
        enableHiding: false,
        cell: ({ row }) => (
          <CloneButton id={row.original.id} onCloned={refreshList} />
        ),
      },
    ],
    [refreshList]
  );

  const handleLoadData = useCallback(
    (state: TableState) => {
      const filterParams = columnFiltersToParams(state.columnFilters);
      const sort = state.sorting[0];
      setSearchPayload((current) => ({
        ...current,
        ...filterParams,
        page: state.page,
        pageSize: state.pageSize,
        text: state.globalFilter || undefined,
        sortAttribute: sort ? (SORT_FIELD_MAP[sort.id] ?? sort.id) : undefined,
        sortDirection: sort ? (sort.desc ? 'DESC' : 'ASC') : undefined,
      }));
    },
    [setSearchPayload]
  );

  const results = (searchResult.data?.results ??
    []) as DifferentialScenarioRow[];
  const totalCount = searchResult.data?.totalCount ?? 0;

  return (
    <div className={styles.contentWrapper}>
      <AtomisedNotifications
        groupIds={[NotificationGroup.DifferentialScenarios]}
      />
      <DifferentialSummaryCards
        aggregations={searchResult.data?.aggregations}
      />
      <div className={styles.tableWrapper}>
        <DataTable<DifferentialScenarioRow>
          data={results}
          columns={columns}
          getRowId={(row) => String(row.id)}
          persistenceKey="differential-scenarios-list-table-v2"
          isLoading={searchResult.loading}
          error={searchResult.error ?? null}
          scroll={{ mode: 'paginated', serverDriven: true, totalCount }}
          onPaginationChange={handleLoadData}
          onSortChange={handleLoadData}
          onGlobalSearchChange={handleLoadData}
          onFilterChange={handleLoadData}
          searchPlaceholder="Search by name or creator..."
          renderFilterPanel={() => (
            <DifferentialScenariosFilters facets={searchResult.data?.facets} />
          )}
        />
      </div>
    </div>
  );
};

export default DifferentialScenariosContent;
